import React, { useState, useEffect, useCallback } from 'react';
import { useDebounce } from '../hooks/useDebounce';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { validateInvite, checkCoupon, createOrder, createStripeSession, createBalanceOrder, subscribeRestockNotify, checkRestockStatus } from '../utils/api';

const CRYPTO_OPTIONS = [
  { id: 'BTC', label: 'Bitcoin', icon: <svg width="20" height="20" viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#F7931A"/><path d="M22.5 14.2c.3-2-1.2-3.1-3.3-3.8l.7-2.7-1.7-.4-.7 2.6c-.4-.1-.9-.2-1.4-.3l.7-2.7-1.7-.4-.7 2.7c-.4-.1-.7-.2-1-.2l-2.3-.6-.4 1.8s1.2.3 1.2.3c.7.2.8.6.8 1l-.8 3.3c0 .1.1.1.1.1l-.1 0-1.2 4.6c-.1.2-.3.6-.8.4 0 0-1.2-.3-1.2-.3l-.8 1.9 2.2.5c.4.1.8.2 1.2.3l-.7 2.8 1.7.4.7-2.7c.5.1.9.2 1.4.3l-.7 2.7 1.7.4.7-2.8c2.9.5 5.1.3 6-2.3.7-2.1 0-3.3-1.5-4.1 1.1-.3 1.9-1 2.1-2.5zm-3.8 5.3c-.5 2.1-4.1 1-5.3.7l.9-3.8c1.2.3 4.9.9 4.4 3.1zm.5-5.4c-.5 1.9-3.5.9-4.4.7l.8-3.4c1 .2 4.1.7 3.6 2.7z" fill="white"/></svg> },
  { id: 'ETH', label: 'Ethereum', icon: <svg width="20" height="20" viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#627EEA"/><path d="M16.5 4v8.9l7.5 3.3L16.5 4z" fill="white" fillOpacity=".6"/><path d="M16.5 4L9 16.2l7.5-3.3V4z" fill="white"/><path d="M16.5 21.9v6.1l7.5-10.4-7.5 4.3z" fill="white" fillOpacity=".6"/><path d="M16.5 28v-6.1L9 17.6l7.5 10.4z" fill="white"/></svg> },
  { id: 'LTC', label: 'Litecoin', icon: <svg width="20" height="20" viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#345D9D"/><path d="M10.5 23.5h11.3l.9-3.2h-7l1.6-5.7 3-1.1.6-2.2-3 1.1L19.6 5h-3.8l-2.3 8.4-3 1.1-.6 2.2 3-1.1-1.4 4.7v3.2z" fill="white"/></svg> },
  { id: 'USDT_ERC20', label: 'USDT ERC20', icon: <svg width="20" height="20" viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#26A17B"/><path d="M17.9 17.1v0c-.1 0-.7.1-2 .1-1 0-1.7 0-1.9-.1v0c-3.8-.2-6.6-.9-6.6-1.7s2.8-1.5 6.6-1.7v2.7c.3 0 1 .1 2 .1 1.2 0 1.7-.1 1.9-.1v-2.7c3.8.2 6.6.9 6.6 1.7s-2.8 1.6-6.6 1.7zm0-3.7V11h5.3V8h-14.5v3h5.3v2.4c-4.3.2-7.5 1.1-7.5 2.2s3.2 2 7.5 2.2v7.9h3.9v-7.9c4.2-.2 7.5-1.1 7.5-2.2s-3.2-2-7.5-2.2z" fill="white"/></svg> },
  { id: 'USDC_ERC20', label: 'USDC ERC20', icon: <svg width="20" height="20" viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#2775CA"/><path d="M20.6 18.5c0-2.1-1.3-2.8-3.8-3.1-1.8-.3-2.2-.7-2.2-1.4s.6-1.2 1.8-1.2c1.1 0 1.7.4 2 1.2h1.8c-.3-1.5-1.4-2.5-3-2.8V9.5h-2v1.7c-1.8.3-2.9 1.5-2.9 3 0 2 1.2 2.7 3.7 3.1 1.8.3 2.3.7 2.3 1.5s-.8 1.3-1.9 1.3c-1.4 0-2-.6-2.2-1.5h-1.8c.2 1.7 1.3 2.7 3.1 3v1.8h2v-1.8c1.8-.3 3.1-1.4 3.1-3.1z" fill="white"/></svg> },
  { id: 'USDT_BEP20', label: 'USDT BEP20', icon: <svg width="20" height="20" viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#F0B90B"/><path d="M17.9 17.1v0c-.1 0-.7.1-2 .1-1 0-1.7 0-1.9-.1v0c-3.8-.2-6.6-.9-6.6-1.7s2.8-1.5 6.6-1.7v2.7c.3 0 1 .1 2 .1 1.2 0 1.7-.1 1.9-.1v-2.7c3.8.2 6.6.9 6.6 1.7s-2.8 1.6-6.6 1.7zm0-3.7V11h5.3V8h-14.5v3h5.3v2.4c-4.3.2-7.5 1.1-7.5 2.2s3.2 2 7.5 2.2v7.9h3.9v-7.9c4.2-.2 7.5-1.1 7.5-2.2s-3.2-2-7.5-2.2z" fill="white"/></svg> }
];

const ProductCard = ({ product, isPopular, siteConfig }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [quantity, setQuantity] = useState(null);
  const [showAllOptions, setShowAllOptions] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [nickname, setNickname] = useState('');
  const [bio, setBio] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [bannerUrl, setBannerUrl] = useState('');
  const [billingType, setBillingType] = useState('one-time');
  const [notifyEnabled, setNotifyEnabled] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  
  const [email, setEmail] = useState('');
  const [serverInvite, setServerInvite] = useState('');
  const [coupon, setCoupon] = useState('');
  const [selectedCrypto, setSelectedCrypto] = useState('LTC');
  const [paymentMethod, setPaymentMethod] = useState('crypto');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [couponExpanded, setCouponExpanded] = useState(false);
  const [cryptoCollapsed, setCryptoCollapsed] = useState(true);
  const [annualMonths, setAnnualMonths] = useState(12);

  const [emailValid, setEmailValid] = useState(null);
  const [inviteStatus, setInviteStatus] = useState({ loading: false, valid: null, error: null, guild: null });
  const [couponStatus, setCouponStatus] = useState({ loading: false, valid: null, discount: null, type: null });
  const [orderLoading, setOrderLoading] = useState(false);

  const debouncedInvite = useDebounce(serverInvite, 2000);

  // Auto-fill email if logged in
  useEffect(() => {
    if (user && user.email && !email) {
      setEmail(user.email);
      setEmailValid(true);
    }
  }, [user]); // eslint-disable-line

  // Check restock notify status from both subscription and user notification prefs
  useEffect(() => {
    if (product.stock === 0 && email && email.includes('@')) {
      checkRestockStatus(product.id, email).then(data => {
        if (data.subscribed) setNotifyEnabled(true);
      }).catch(() => {});
    }
  }, [product.id, product.stock, email]);

  // Also check user notification prefs for initial state
  useEffect(() => {
    if (user && user.notificationPrefs) {
      // If user has explicitly disabled restock alerts or this product's alerts, reflect that
      if (user.notificationPrefs.restockAlerts === false) {
        setNotifyEnabled(false);
      }
    }
  }, [user]);

  const isOutOfStock = product.stock === 0;
  const currentStock = product.stock || 0;
  const isReseller = user && user.reseller;
  const pricePerBoost = isReseller && product.resellerPricePerBoost ? product.resellerPricePerBoost : (product.pricePerBoost || 0.20);

  const allOptions = product.allBoostOptions || [2,4,6,8,10,12,14,16,18,20,22,24,26,28,30];
  const defaultOptions = product.boostOptions || [2,8,14,20,30];
  const boostOptions = showAllOptions ? allOptions : defaultOptions;
  const hasMoreOptions = allOptions.length > defaultOptions.length;

  useEffect(() => {
    if (quantity === null || quantity > currentStock) {
      const availableOptions = defaultOptions.filter(opt => opt <= currentStock);
      if (availableOptions.length > 0) setQuantity(availableOptions[0]);
      else if (currentStock > 0) {
        const allAvailable = allOptions.filter(opt => opt <= currentStock);
        if (allAvailable.length > 0) setQuantity(allAvailable[0]);
        else setQuantity(currentStock);
      } else setQuantity(2);
    }
  }, [currentStock, quantity, defaultOptions, allOptions]);

  const effectiveQuantity = quantity || 2;
  const subtotal = effectiveQuantity * pricePerBoost;
  const annualMultiplier = billingType === 'annual' ? 12 : 1;
  const ANNUAL_PRICE_PER_BOOST = 4.28;
  
  let couponDiscount = 0;
  if (couponStatus.valid) {
    couponDiscount = couponStatus.type === 'percentage' ? subtotal * (couponStatus.discount / 100) : couponStatus.discount;
  }
  
  const CRYPTO_MINIMUMS = { 'BTC': 5.00, 'ETH': 3.00, 'LTC': 0.50, 'USDT_ERC20': 1.00, 'USDC_ERC20': 1.00, 'USDT_BEP20': 0.50 };
  
  const annualTotal = parseFloat((effectiveQuantity * ANNUAL_PRICE_PER_BOOST).toFixed(2));
  const baseTotal = billingType === 'annual' ? annualTotal : (subtotal - couponDiscount);
  const minAmount = paymentMethod === 'crypto' ? (CRYPTO_MINIMUMS[selectedCrypto] || 0.50) : 0.50;
  const minimumFee = paymentMethod === 'crypto' && baseTotal < minAmount ? minAmount - baseTotal : 0;
  const total = baseTotal + minimumFee;
  const total2 = subtotal - couponDiscount;

  // Stripe fee: 2.9% + $0.30, enforced minimum $1.50
  const STRIPE_FEE_PERCENT = 0.029;
  const STRIPE_FEE_FIXED = 0.30;
  const STRIPE_MINIMUM = 1.50;
  const stripeFeeRaw = parseFloat((baseTotal * STRIPE_FEE_PERCENT + STRIPE_FEE_FIXED).toFixed(2));
  const stripeSubtotal = parseFloat((baseTotal + stripeFeeRaw).toFixed(2));
  const stripeTotal = Math.max(stripeSubtotal, STRIPE_MINIMUM);

  const validateEmailFormat = (em) => em.includes('@') && em.length > 3;

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setEmailValid(value.length > 0 ? validateEmailFormat(value) : null);
  };

  const validateInviteLink = useCallback(async (invite) => {
    if (!invite || invite.length < 2) { setInviteStatus({ loading: false, valid: null, error: null, guild: null }); return; }
    setInviteStatus({ loading: true, valid: null, error: null, guild: null });
    try {
      const result = await validateInvite(invite);
      setInviteStatus(result.valid ? { loading: false, valid: true, error: null, guild: result.guild } : { loading: false, valid: false, error: result.error, guild: null });
    } catch (err) {
      setInviteStatus({ loading: false, valid: false, error: 'Failed to validate invite', guild: null });
    }
  }, []);

  useEffect(() => { if (debouncedInvite) validateInviteLink(debouncedInvite); }, [debouncedInvite, validateInviteLink]);

  const handleInviteChange = (e) => {
    const value = e.target.value;
    setServerInvite(value);
    setInviteStatus(value.length > 0 ? { loading: true, valid: null, error: null, guild: null } : { loading: false, valid: null, error: null, guild: null });
  };

  const handleCouponCheck = async () => {
    if (!coupon) return;
    setCouponStatus({ loading: true, valid: null, discount: null, type: null });
    try {
      const result = await checkCoupon(coupon);
      if (result.resellerBlocked) {
        setCouponStatus({ loading: false, valid: false, discount: null, type: null, resellerBlocked: true });
      } else {
        setCouponStatus(result.valid ? { loading: false, valid: true, discount: result.discount, type: result.type } : { loading: false, valid: false, discount: null, type: null });
      }
    } catch (err) {
      setCouponStatus({ loading: false, valid: false, discount: null, type: null });
    }
  };

  const handleNotifyMe = async () => {
    if (!email || !validateEmailFormat(email)) return;
    setNotifyLoading(true);
    try {
      await subscribeRestockNotify(product.id, email);
      setNotifyEnabled(true);
    } catch (err) { console.error(err); }
    finally { setNotifyLoading(false); }
  };

  const canOpenModal = !isOutOfStock && emailValid && inviteStatus.valid && !inviteStatus.loading;
  const canCreateOrder = termsAccepted && !orderLoading;

  const handleBuyNow = () => { if (canOpenModal) setShowModal(true); };

  const handleCreateOrder = async () => {
    if (!canCreateOrder) return;
    setOrderLoading(true);
    try {
      const orderData = { 
        email, 
        serverInvite, 
        productId: product.id, 
        quantity: effectiveQuantity, 
        coupon: couponStatus.valid ? coupon : null, 
        nickname: nickname || undefined, 
        bio: bio || undefined, 
        avatar: avatarUrl || undefined, 
        banner: bannerUrl || undefined, 
        billingType, 
        annualMonths: billingType === 'annual' ? 12 : undefined 
      };
      
      if (paymentMethod === 'balance') {
        const result = await createBalanceOrder(orderData);
        if (result.success) { 
          window.location.href = `/checkout/${result.orderId}`;
          resetModal(); 
        }
      } else if (paymentMethod === 'fiat') {
        const result = await createStripeSession(orderData);
        if (result.success && result.stripeUrl) { 
          window.location.href = result.stripeUrl; 
        }
      } else {
        const result = await createOrder({ ...orderData, cryptoType: selectedCrypto });
        if (result.success) { 
          window.location.href = `/checkout/${result.orderId}`;
          resetModal(); 
        }
      }
    } catch (err) { 
      alert(err.message || 'Failed to create order'); 
    }
    finally { 
      setOrderLoading(false); 
    }
  };

  const resetModal = () => { setShowModal(false); setTermsAccepted(false); setShowCustomize(false); setCouponExpanded(false); setCryptoCollapsed(false); };

  const isCardDisabled = siteConfig?.paymentMethods?.stripe?.enabled === false;
  const isCryptoDisabled = siteConfig?.paymentMethods?.crypto?.enabled === false;

  return (
    <>
      <div className={`product-card ${isPopular ? 'popular' : ''} ${isOutOfStock ? 'out-of-stock' : ''}`}>
        {isOutOfStock && (<div className="out-of-stock-overlay"></div>)}
        {isOutOfStock && (<div className="product-card-overlay-section"><div className="out-of-stock-banner" style={{position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%) rotate(-35deg)',zIndex:11}}>OUT OF STOCK</div></div>)}
        {isPopular && (<div className="popular-badge"><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>Popular</div>)}
        
        <div className="product-header">
          <div><div className="product-title">{product.product}</div><div className="product-type">Server Boosts</div></div>
          <span className={`stock-badge ${isOutOfStock ? 'out-of-stock' : 'in-stock'}`}>
            <span className="stock-dot"></span>
            {isOutOfStock ? t('products.outOfStock') : `${product.stock} in stock`}
          </span>
        </div>

        <div className="pc-tier-bar">
          <span className={`pc-tier ${effectiveQuantity >= 2 ? 'pc-tier-on' : ''}`}>Level 1</span>
          <span className={`pc-tier ${effectiveQuantity >= 7 ? 'pc-tier-mid' : ''}`}>Level 2</span>
          <span className={`pc-tier ${effectiveQuantity >= 14 ? 'pc-tier-max' : ''}`}>Level 3</span>
        </div>

        <div className="boost-selector">
          <div className="boost-selector-label">{t('products.amountOfBoosts')}</div>
          <div className="boost-options">
            {boostOptions.map((opt) => (
              <button key={opt} className={`boost-option ${quantity === opt ? 'selected' : ''} ${opt > currentStock ? 'unavailable' : ''}`}
                onClick={() => setQuantity(opt)} disabled={isOutOfStock || opt > currentStock}>{opt}</button>
            ))}
          </div>
          {hasMoreOptions && (<button className="show-more-btn" onClick={() => setShowAllOptions(!showAllOptions)}>{showAllOptions ? t('products.showLess') + ' ▲' : t('products.showMore') + ' ▼'}</button>)}
        </div>

                <div className="price-display">
          <span className="price-amount">${total2.toFixed(2)}</span>
          <div className="price-meta">
            <span className="price-label">{t('products.total')}</span>
            <span className="price-per">${pricePerBoost.toFixed(2)}/boost{isReseller && <span style={{color:'#a855f7',marginLeft:'0.25rem',fontSize:'0.7rem'}}>Reseller</span>}</span>
          </div>
        </div>

        <div className="features-list">
          <div className="feature-item"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>{t('products.instantDelivery')}</div>
          <div className="feature-item"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>{t('products.securePayment')}</div>
          <div className="feature-item"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>{t('products.support247')}</div>
        </div>

        <div className="form-group">
          <label className="form-label">{t('products.email')} <span className="required">*</span></label>
          <input type="email" className={`form-input ${emailValid === false ? 'error' : ''} ${emailValid === true ? 'success' : ''}`} placeholder="your@email.com" value={email} onChange={handleEmailChange} disabled={isOutOfStock || (user && user.email)} style={user && user.email ? {opacity:0.6} : {}} />
          {emailValid === false && (<div className="input-message error">✕ {t('products.invalidEmail')}</div>)}
        </div>

        <div className="form-group">
          <label className="form-label">{t('products.discordInvite')} <span className="required">*</span></label>
          <button type="button" className="customize-toggle-btn" onClick={() => setShowCustomize(!showCustomize)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
            {t('products.customizeBoosts')}
          </button>
          {showCustomize && (
            <div className="customize-popup">
              <div className="customize-popup-inner">
                <div className="customize-field">
                  <label className="form-label">{t('products.nickname')} <span className="optional-tag">{t('products.optional')}</span></label>
                  <input type="text" className="form-input" placeholder="Account nickname" value={nickname} onChange={(e) => setNickname(e.target.value)} maxLength={32} />
                </div>
                <div className="customize-field">
                  <label className="form-label">{t('products.bio')} <span className="optional-tag">{t('products.optional')}</span></label>
                  <input type="text" className="form-input" placeholder="Account bio / about me" value={bio} onChange={(e) => setBio(e.target.value)} maxLength={190} />
                </div>
                <div style={{display:'flex',gap:'0.5rem',marginTop:'0.5rem'}}>
                  <div className="customize-field" style={{flex:1}}>
                    <label className="form-label" style={{fontSize:'0.7rem'}}>Change Avatar <span className="optional-tag">{t('products.optional')}</span></label>
                    <input type="text" className="form-input" placeholder="Image URL" value={avatarUrl} onChange={(e) => setAvatarUrl(e.target.value)} style={{fontSize:'0.78rem',padding:'0.5rem 0.6rem'}} />
                    {!avatarUrl && <span style={{fontSize:'0.65rem',color:'var(--text-muted)',opacity:0.6,marginTop:'0.15rem',display:'block'}}>No avatar is set</span>}
                  </div>
                  <div className="customize-field" style={{flex:1}}>
                    <label className="form-label" style={{fontSize:'0.7rem'}}>Change Banner <span className="optional-tag">{t('products.optional')}</span></label>
                    <input type="text" className="form-input" placeholder="Image URL" value={bannerUrl} onChange={(e) => setBannerUrl(e.target.value)} style={{fontSize:'0.78rem',padding:'0.5rem 0.6rem'}} />
                    {!bannerUrl && <span style={{fontSize:'0.65rem',color:'var(--text-muted)',opacity:0.6,marginTop:'0.15rem',display:'block'}}>No banner is set</span>}
                  </div>
                </div>
              </div>
            </div>
          )}
          <input type="text" className={`form-input ${inviteStatus.valid === false ? 'error' : ''} ${inviteStatus.valid === true ? 'success' : ''}`} placeholder="discord.gg/invite or invite code" value={serverInvite} onChange={handleInviteChange} disabled={isOutOfStock} />
          {inviteStatus.loading && (<div className="input-message loading"><span className="loading-spinner"></span> {t('products.validatingInvite')}</div>)}
          {inviteStatus.valid === false && (<div className="input-message error">✕ {inviteStatus.error || 'Invalid invite link'}</div>)}
          {inviteStatus.valid === true && inviteStatus.guild && (
            <div className="server-info">
              <div className="server-icon">{inviteStatus.guild.icon ? <img src={`https://cdn.discordapp.com/icons/${inviteStatus.guild.id}/${inviteStatus.guild.icon}.png`} alt={inviteStatus.guild.name}/> : '🎮'}</div>
              <div className="server-details"><div className="server-name">{inviteStatus.guild.name}</div><div className="server-members">{inviteStatus.guild.memberCount?.toLocaleString()} members</div></div>
            </div>
          )}
        </div>

        {isOutOfStock ? (
          <button style={{position:'relative',zIndex:11}} className={`btn ${notifyEnabled ? 'btn-notify-active' : 'btn-notify'}`} onClick={handleNotifyMe} disabled={notifyEnabled || notifyLoading || !emailValid}>
            {notifyLoading ? <span className="loading-spinner"></span> : notifyEnabled ? (
              <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>{t('products.notifyMeEnabled')}</>
            ) : (
              <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>{t('products.notifyMe')}</>
            )}
          </button>
        ) : (
          <button className="btn btn-primary" onClick={handleBuyNow} disabled={!canOpenModal}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>{`${t('products.buyNow')} — $${total2.toFixed(2)}`}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
          </button>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{effectiveQuantity}x [{billingType === 'annual' ? 'Annual ~ 1 Year' : product.product}] Server Boost</h2>
              <button className="modal-close" onClick={resetModal}><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
            </div>
            
            <div className="modal-body">
              {/* Billing type toggle */}
              <div className="billing-toggle-section">
                <div className="billing-toggle-buttons">
                  <button className={`billing-toggle-btn ${billingType === 'one-time' ? 'selected' : ''}`} onClick={() => setBillingType('one-time')}>{t('products.oneTime')}</button>
                  <button className={`billing-toggle-btn annual ${billingType === 'annual' ? 'selected' : ''}`} onClick={() => setBillingType('annual')}>{t('products.annual')} <span className="annual-save-badge">{t('products.annualSaving')}</span></button>
                </div>
              </div>

              {/* Annual fixed info */}
              {billingType === 'annual' && (
                <div className="annual-months-section">
                  <div className="annual-months-label">
                    <label className="form-label" style={{marginBottom:0}}>Billing Duration</label>
                    <span className="annual-months-value">12 months</span>
                  </div>
                  <div style={{fontSize:'0.8rem',color:'var(--text-muted)',marginTop:'0.25rem'}}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{verticalAlign:'middle',marginRight:4}}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    Boosts are automatically re-applied every 3 months
                  </div>
                </div>
              )}

              {/* Simplified payment method */}
              <div className="payment-method-toggle">
                <label className="form-label">{t('checkout.selectPayment')}</label>
                <div className="payment-method-buttons-v2">
                  <div className="pm-btn-wrap" data-tooltip={isCardDisabled ? 'Card payments are currently unavailable' : undefined}>
                    <button className={`pm-btn-v2 ${paymentMethod === 'fiat' ? 'selected' : ''} ${isCardDisabled ? 'pm-disabled' : ''}`} onClick={() => !isCardDisabled && setPaymentMethod('fiat')} disabled={isCardDisabled}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                      <span>{t('checkout.card')}</span>
                    </button>
                  </div>
                  {(() => {
                    const balanceTotal = billingType === 'annual' ? annualTotal : Math.max(subtotal - couponDiscount, 0.01);
                    const userBal = user ? (user.balance || 0) : 0;
                    const hasEnough = user && userBal >= balanceTotal;
                    const isBalDisabled = !user || !hasEnough;
                    const balTooltip = !user ? 'Log in to use balance' : !hasEnough ? `Need $${balanceTotal.toFixed(2)}, have $${userBal.toFixed(2)}` : undefined;
                    return (
                      <div className="pm-btn-wrap" data-tooltip={balTooltip}>
                        <button className={`pm-btn-v2 ${paymentMethod === 'balance' ? 'selected' : ''} ${isBalDisabled ? 'pm-disabled' : ''}`} onClick={() => !isBalDisabled && setPaymentMethod('balance')} disabled={isBalDisabled}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><path d="M18 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/></svg>
                          <span>Balance {user && <span style={{fontSize:'0.72rem',opacity:0.6}}>${userBal.toFixed(2)}</span>}</span>
                        </button>
                      </div>
                    );
                  })()}
                  <div className="pm-btn-wrap" data-tooltip={isCryptoDisabled ? 'Crypto payments are currently unavailable' : undefined}>
                    <button className={`pm-btn-v2 ${paymentMethod === 'crypto' ? 'selected' : ''} ${isCryptoDisabled ? 'pm-disabled' : ''}`} onClick={() => !isCryptoDisabled && setPaymentMethod('crypto')} disabled={isCryptoDisabled}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.5 8h5a2.5 2.5 0 010 5h-5V8z"/><path d="M9.5 13h5.5a2.5 2.5 0 010 5H9.5V13z"/></svg>
                      <span>{t('checkout.crypto')}</span>
                    </button>
                  </div>
                </div>
              </div>

              {paymentMethod === 'crypto' && (
                <div className="crypto-selector">
                  <label className="form-label">{t('checkout.selectCrypto')}</label>
                  {cryptoCollapsed ? (
                    <div className="crypto-selected-display">
                      <div className="crypto-selected-info">
                        <span className="crypto-option-icon">{CRYPTO_OPTIONS.find(c => c.id === selectedCrypto)?.icon}</span>
                        <span>{CRYPTO_OPTIONS.find(c => c.id === selectedCrypto)?.label}</span>
                      </div>
                      <button className="crypto-change-btn" onClick={() => setCryptoCollapsed(false)}>Change</button>
                    </div>
                  ) : (
                    <div className="crypto-options">
                      {CRYPTO_OPTIONS.map((crypto) => (
                        <button key={crypto.id} className={`crypto-option ${selectedCrypto === crypto.id ? 'selected' : ''}`} onClick={() => { setSelectedCrypto(crypto.id); setCryptoCollapsed(true); }}>
                          <span className="crypto-option-icon">{crypto.icon}</span>
                          <span className="crypto-option-label">{crypto.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="form-group">
                <button type="button" className={`coupon-expand-btn ${couponExpanded ? 'open' : ''}`} onClick={() => setCouponExpanded(!couponExpanded)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                  {couponStatus.valid ? `Coupon applied: ${couponStatus.discount}${couponStatus.type === 'percentage' ? '%' : '$'} off` : 'Have a coupon?'}
                </button>
                {couponExpanded && (
                  <>
                    <div className="coupon-row">
                      <input type="text" className={`form-input ${couponStatus.valid === false ? 'error' : ''} ${couponStatus.valid === true ? 'success' : ''}`} placeholder={t('checkout.enterCoupon')} value={coupon}
                        onChange={(e) => { setCoupon(e.target.value); setCouponStatus({ loading: false, valid: null, discount: null, type: null }); }} />
                      <button className="coupon-btn" onClick={handleCouponCheck} disabled={!coupon || couponStatus.loading}>{couponStatus.loading ? '...' : t('checkout.apply')}</button>
                    </div>
                    {couponStatus.valid === false && (<div className="input-message error">✕ {couponStatus.resellerBlocked ? 'Coupon is not available for resellers' : t('checkout.invalidCoupon')}</div>)}
                    {couponStatus.valid === true && (<div className="input-message success">✓ {t('checkout.couponApplied')}: {couponStatus.discount}{couponStatus.type === 'percentage' ? '%' : '$'} off</div>)}
                  </>
                )}
              </div>

              <div className="price-summary">
                <div className="price-row"><span className="label">{effectiveQuantity}x {t('checkout.boosts')}{billingType === 'annual' ? ` — Annual ~ 1 Year` : ''}</span><span className="value">${baseTotal.toFixed(2)}</span></div>
                {billingType === 'annual' && (<div className="price-row"><span className="label" style={{fontSize:'0.75rem',color:'var(--text-muted)'}}>Reboosts every 3 months automatically</span><span className="value" style={{fontSize:'0.75rem',color:'var(--text-muted)'}}>4 cycles</span></div>)}
                {billingType === 'annual' && (<div className="price-row"><span className="label" style={{fontSize:'0.75rem',color:'var(--text-muted)'}}>Price per boost</span><span className="value" style={{fontSize:'0.75rem',color:'var(--text-muted)'}}>${ANNUAL_PRICE_PER_BOOST.toFixed(2)}/yr</span></div>)}
                {couponStatus.valid && couponDiscount > 0 && billingType !== 'annual' && (<div className="price-row"><span className="label">{t('checkout.couponDiscount')}</span><span className="value discount">-${(couponDiscount * annualMultiplier).toFixed(2)}</span></div>)}
                {paymentMethod === 'crypto' && minimumFee > 0 && (<div className="price-row"><span className="label">{t('checkout.cryptoFee')}</span><span className="value fee">+${minimumFee.toFixed(2)}</span></div>)}
                {paymentMethod === 'fiat' && (<div className="price-row"><span className="label">Processing Fee (Stripe)</span><span className="value fee">+${stripeFeeRaw.toFixed(2)}</span></div>)}
                <div className="price-row total">
                  <span className="label">{t('checkout.totalLabel')}</span>
                  <span className="value">
                    ${paymentMethod === 'crypto' ? total.toFixed(2) : paymentMethod === 'balance' ? baseTotal.toFixed(2) : stripeTotal.toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="checkbox-row" onClick={() => setTermsAccepted(!termsAccepted)}>
                <div className={`checkbox ${termsAccepted ? 'checked' : ''}`}>{termsAccepted && (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>)}</div>
                <span className="checkbox-label">{t('checkout.termsAgree')}{' '}<a href="/terms" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{t('checkout.termsOfService')}</a></span>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={resetModal}>{t('checkout.cancel')}</button>
              <button className="btn btn-primary" onClick={handleCreateOrder} disabled={!canCreateOrder}>{orderLoading ? <span className="loading-spinner"></span> : paymentMethod === 'balance' ? `Pay with Balance — $${(billingType === 'annual' ? annualTotal : Math.max(subtotal - couponDiscount, 0.01)).toFixed(2)}` : paymentMethod === 'fiat' ? `Pay $${stripeTotal.toFixed(2)} with Stripe` : t('checkout.payNow')}</button>
            </div>
            {paymentMethod !== 'balance' && (
              <div style={{ textAlign: 'center', padding: '10px 0 14px', fontSize: '0.7rem', color: 'rgba(255,255,255,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', borderTop: '1px solid rgba(255,255,255,0.05)', marginTop: 2 }}>
                Powered by <span style={{ fontWeight: 700, color: 'rgba(255,255,255,0.4)' }}>{paymentMethod === 'fiat' ? 'Stripe' : 'NowPayments.io'}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ProductCard;